import {
  ChatInteractContextschemaSurfaceEnum,
  TopologyArtifactOverlayStateschemaSchemaEnum,
  TopologyOverlayBaseschemaSourceEnum,
  TopologyOverlayBaseschemaTimeModeEnum
} from '@app/types/sdk';
import type {
  ChatInteractContextschema,
  ChatVisibilitySessionContextschema,
  TopologyArtifactOverlayLayerStatusschema,
  TopologyOverlayBaseschema,
  TopologyOverlayLayerschema,
  TopologyOverlaySelectionschema,
  TopologyV2Graphschema,
  TopologyV2ResolveRequest,
  VisibilityProposal,
  VisibilitySelectedElementschema,
  VisibilitySession,
  VisibilitySessionStateschema
} from '@app/types/sdk';
import {
  applyTopologyOverlayPatch,
  emptyTopologyOverlayState,
  hydrateTopologyOverlayState,
  type TopologyOverlayRuntimeState
} from '@app/utils/topologyOverlay';

export interface TopologyWorkbenchSelectedElement {
  kind: 'node' | 'edge';
  id: string;
}

export interface TopologyWorkbenchResumeState {
  sessionId?: string;
  threadId?: string;
  bindingHash?: string;
}

export interface VisibilitySessionStateEventData {
  sessionId: string;
  workspaceFamily: string;
  status: string;
  mode: string;
  bindingHash: string;
  revision: number;
  historyDepth?: number;
  chatThreadId?: string;
  pendingProposalId?: string;
  visibleState: VisibilitySessionStateschema;
}

export interface TopologyWorkbenchBindingDraft {
  viewId: string;
  scopeType: string;
  scopeId: string;
  params: Record<string, string | boolean>;
  limits: Record<string, unknown>;
  asOf?: string | null;
}

export interface VisibilityOverlayStatusEventData {
  sessionId: string;
  revision: number;
  layers: TopologyArtifactOverlayLayerStatusschema[];
}

export interface TopologyWorkbenchStreamFrame {
  type: string;
  payload: Record<string, unknown>;
  seq?: number;
}

/** Selects a settled workbench graph, preserving intentional empty results. */
export const selectTopologyWorkbenchGraph = (
  runtimeGraph: TopologyV2Graphschema | null | undefined,
  derivedGraph: TopologyV2Graphschema | null | undefined,
  savedGraph: TopologyV2Graphschema | null | undefined,
  resolvedGraph: TopologyV2Graphschema | null | undefined,
  runtimeGraphSettled = true
): TopologyV2Graphschema | null =>
  (runtimeGraphSettled ? runtimeGraph : null) ?? derivedGraph ?? savedGraph ?? resolvedGraph ?? null;

const STORAGE_PREFIX = 'reap.topology.workbench.resume';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

const stableJson = (value: unknown): string => {
  if (Array.isArray(value)) {
    return `[${value.map((item) => stableJson(item)).join(',')}]`;
  }
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${stableJson(value[key])}`)
      .join(',')}}`;
  }
  return JSON.stringify(value);
};

const nonEmptyString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const formatSemanticSelectorValue = (value: unknown): string | null => {
  if (typeof value === 'string') {
    return nonEmptyString(value);
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  if (!isRecord(value)) {
    return null;
  }
  const typed = value as { stringValue?: unknown; numberValue?: unknown; booleanValue?: unknown };
  if (typeof typed.stringValue === 'string') {
    return nonEmptyString(typed.stringValue);
  }
  if (typeof typed.numberValue === 'number' && Number.isFinite(typed.numberValue)) {
    return String(typed.numberValue);
  }
  if (typeof typed.booleanValue === 'boolean') {
    return typed.booleanValue ? 'true' : 'false';
  }
  return null;
};

const formatRelationshipId = (relationshipId: string): string => {
  const tail = relationshipId.replace(/^topology\.relationship\./, '');
  return tail
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => {
      const upper = part.toUpperCase();
      if (['LAG', 'VLAN', 'VPN', 'L2', 'L3', 'STP'].includes(upper)) {
        return upper;
      }
      return `${part.slice(0, 1).toUpperCase()}${part.slice(1)}`;
    })
    .join(' ');
};

const formatRenderMode = (renderMode: string): string =>
  renderMode
    .split('_')
    .filter(Boolean)
    .join(' ');

const countStringCollection = (value: unknown): number => {
  if (value instanceof Set) {
    return Array.from(value).filter((item) => typeof item === 'string' && item.trim().length > 0).length;
  }
  if (Array.isArray(value)) {
    return value.filter((item) => typeof item === 'string' && item.trim().length > 0).length;
  }
  return 0;
};

export const formatTopologyRelationshipLayerMetadata = (
  layer: Pick<TopologyOverlayLayerschema, 'relationship'> | undefined
): string | null => {
  const relationship = layer?.relationship;
  const relationshipId = nonEmptyString(relationship?.relationshipId);
  if (!relationshipId) {
    return null;
  }

  const parts = [`Relationship: ${formatRelationshipId(relationshipId)}`];
  const renderMode = nonEmptyString(relationship?.renderMode);
  if (renderMode) {
    parts.push(`mode ${formatRenderMode(renderMode)}`);
  }

  const qualifiers = relationship?.qualifiers;
  if (qualifiers && Object.keys(qualifiers).length > 0) {
    const renderedQualifiers = Object.entries(qualifiers)
      .map(([key, value]) => {
        const renderedValue = formatSemanticSelectorValue(value);
        return renderedValue ? `${key}=${renderedValue}` : null;
      })
      .filter((value): value is string => Boolean(value));
    if (renderedQualifiers.length > 0) {
      parts.push(`qualifiers ${renderedQualifiers.join(', ')}`);
    }
  }

  const sourceSummary = nonEmptyString(relationship?.sourceSummary);
  if (sourceSummary) {
    parts.push(`source ${sourceSummary}`);
  }

  const catalogIdCount = countStringCollection(relationship?.catalogIds);
  if (catalogIdCount > 0) {
    parts.push(`${catalogIdCount} catalog ${catalogIdCount === 1 ? 'ID' : 'IDs'}`);
  }

  const activeInstanceId = nonEmptyString(relationship?.activeInstanceId);
  if (activeInstanceId) {
    parts.push(`instance ${activeInstanceId}`);
  }

  return parts.join(' · ');
};

const formatPathToken = (value: string): string =>
  value
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => {
      const upper = part.toUpperCase();
      if (['L2', 'L3', 'ECMP', 'VPN', 'NAT'].includes(upper)) {
        return upper;
      }
      return part;
    })
    .join(' ');

export const formatTopologyPathLayerMetadata = (
  layer: Pick<TopologyOverlayLayerschema, 'path' | 'meta'> | undefined
): string | null => {
  const path = layer?.path;
  if (!path) {
    return null;
  }

  const parts: string[] = [];
  const source = nonEmptyString(path.sourceSummary);
  const destination = nonEmptyString(path.destinationSummary);
  if (source && destination) {
    parts.push(`Path: ${source} to ${destination}`);
  } else {
    parts.push('Path');
  }

  const selectedMode = nonEmptyString(path.selectedMode);
  const requestedMode = nonEmptyString(path.requestedMode);
  if (selectedMode) {
    const requested = requestedMode && requestedMode !== selectedMode ? `, requested ${formatPathToken(requestedMode)}` : '';
    parts.push(`mode ${formatPathToken(selectedMode)}${requested}`);
  } else if (requestedMode) {
    parts.push(`requested ${formatPathToken(requestedMode)}`);
  }

  const evidenceLevel = nonEmptyString(path.evidenceLevel);
  if (evidenceLevel) {
    parts.push(`evidence ${formatPathToken(evidenceLevel)}`);
  }

  const presentation = nonEmptyString(path.renderDecision?.presentation);
  if (presentation) {
    const detail = nonEmptyString(path.renderDecision?.detailLevel);
    parts.push(`render ${formatPathToken(presentation)}${detail ? `/${formatPathToken(detail)}` : ''}`);
  }

  const pathId = nonEmptyString(path.pathId);
  if (pathId) {
    parts.push(`path ${pathId}`);
  }

  const resultScopeId = nonEmptyString(path.resultScopeId);
  if (resultScopeId) {
    parts.push(`scope ${resultScopeId}`);
  }

  return parts.join(' · ');
};

export const formatTopologyPathRelatedViewBreadcrumb = (
  layer: Pick<TopologyOverlayLayerschema, 'path' | 'graphOverlay'> | undefined
): string | null => {
  const path = layer?.path;
  const renderDecision = path?.renderDecision;
  if (renderDecision?.presentation !== 'related_view') {
    return null;
  }
  const relatedViewId = nonEmptyString(renderDecision.relatedViewId) ?? nonEmptyString(layer?.graphOverlay?.viewId) ?? 'path detail';
  const sourceViewId = nonEmptyString(layer?.graphOverlay?.sourceViewId);
  const reason = nonEmptyString(renderDecision.reason);
  const parts = [`Related view: ${relatedViewId}`];
  if (sourceViewId) {
    parts.push(`from ${sourceViewId}`);
  }
  if (reason) {
    parts.push(reason);
  }
  return parts.join(' · ');
};

const sanitizeRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(value) || Object.keys(value).length === 0) {
    return undefined;
  }
  return { ...value };
};

const toDate = (value: unknown): Date | undefined => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }
  const text = nonEmptyString(value);
  if (!text) {
    return undefined;
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const serializeDateLike = (value: unknown): string | null => {
  const date = toDate(value);
  return date ? date.toISOString() : null;
};

const buildStorageKey = (orgId: string, resumeKey: string) =>
  `${STORAGE_PREFIX}:${encodeURIComponent(orgId)}:${encodeURIComponent(resumeKey)}`;

export const buildTopologyWorkbenchBase = (
  viewId: string,
  request: Record<string, unknown> | TopologyV2ResolveRequest
): TopologyOverlayBaseschema => {
  const scopeType = nonEmptyString(request.scopeType);
  const scopeId = nonEmptyString(request.scopeId);
  if (!scopeType || !scopeId) {
    throw new Error('Topology workbench base requires scopeType and scopeId.');
  }

  const asOf = toDate(request.asOf);
  const base: TopologyOverlayBaseschema = {
    source: TopologyOverlayBaseschemaSourceEnum.TopologyService,
    viewId,
    scope: {
      scopeType: scopeType as TopologyOverlayBaseschema['scope']['scopeType'],
      scopeId
    },
    timeMode: asOf ? TopologyOverlayBaseschemaTimeModeEnum.Fixed : TopologyOverlayBaseschemaTimeModeEnum.Current
  };

  const params = sanitizeRecord(request.params);
  if (params) {
    base.params = params;
  }
  const limits = sanitizeRecord(request.limits);
  if (limits) {
    base.limits = limits as TopologyOverlayBaseschema['limits'];
  }
  if (asOf) {
    base.asOf = asOf;
  }
  return base;
};

export const buildTopologyWorkbenchResumeKey = (base: TopologyOverlayBaseschema): string =>
  `topology_workbench:${stableJson({
    source: base.source,
    viewId: base.viewId,
    scope: base.scope,
    params: base.params ?? {},
    limits: base.limits ?? {},
    timeMode: base.timeMode,
    asOf: serializeDateLike(base.asOf)
  })}`;

export const buildTopologyWorkbenchSelection = (
  selectedElements: Iterable<TopologyWorkbenchSelectedElement>
): TopologyOverlaySelectionschema | undefined => {
  const nodeIds = new Set<string>();
  const edgeIds = new Set<string>();
  for (const item of selectedElements) {
    if (!item?.id) {
      continue;
    }
    if (item.kind === 'node') {
      nodeIds.add(item.id);
    } else if (item.kind === 'edge') {
      edgeIds.add(item.id);
    }
  }
  if (nodeIds.size === 0 && edgeIds.size === 0) {
    return undefined;
  }
  return {
    nodeIds: nodeIds.size > 0 ? nodeIds : undefined,
    edgeIds: edgeIds.size > 0 ? edgeIds : undefined
  };
};

export const buildVisibilitySelectedElements = (
  selectedElements: Iterable<TopologyWorkbenchSelectedElement>
): Set<VisibilitySelectedElementschema> | undefined => {
  const out = new Map<string, VisibilitySelectedElementschema>();
  for (const item of selectedElements) {
    if (!item?.id) {
      continue;
    }
    const kind = item.kind === 'node' ? 'topology.node' : item.kind === 'edge' ? 'topology.edge' : '';
    if (!kind) {
      continue;
    }
    out.set(`${kind}:${item.id}`, { kind, id: item.id });
  }
  return out.size > 0 ? new Set(out.values()) : undefined;
};

export const buildTopologyWorkbenchSessionContext = (
  session: VisibilitySession,
  selectedElements: Iterable<TopologyWorkbenchSelectedElement>,
  selectedLayerIds: Iterable<string> = []
): ChatVisibilitySessionContextschema => {
  const layerIds = new Set<string>();
  for (const value of selectedLayerIds) {
    const next = nonEmptyString(value);
    if (next) {
      layerIds.add(next);
    }
  }

  return {
    sessionId: session.id ?? '',
    workspaceFamily: session.workspaceFamily,
    mode: session.mode,
    bindingHash: session.bindingHash,
    revision: session.state?.revision ?? 0,
    pendingProposalId: session.pendingProposalId,
    selectedElements: buildVisibilitySelectedElements(selectedElements),
    selectedLayerIds: layerIds.size > 0 ? layerIds : undefined
  };
};

export const buildTopologyWorkbenchChatInteractContext = (
  session: VisibilitySession,
  selectedElements: Iterable<TopologyWorkbenchSelectedElement>,
  selectedLayerIds: Iterable<string> = []
): ChatInteractContextschema => ({
  surface: ChatInteractContextschemaSurfaceEnum.TopologyPage,
  visibilitySession: buildTopologyWorkbenchSessionContext(session, selectedElements, selectedLayerIds)
});

export const mergeVisibilitySessionStateEvent = (
  session: VisibilitySession,
  event: VisibilitySessionStateEventData
): VisibilitySession => ({
  ...session,
  workspaceFamily: event.workspaceFamily as VisibilitySession['workspaceFamily'],
  status: event.status as VisibilitySession['status'],
  mode: event.mode as VisibilitySession['mode'],
  bindingHash: event.bindingHash,
  chatThreadId: nonEmptyString(event.chatThreadId) ?? session.chatThreadId,
  historyDepth:
    typeof event.historyDepth === 'number' && Number.isFinite(event.historyDepth)
      ? Math.max(0, Math.floor(event.historyDepth))
      : session.historyDepth,
  pendingProposalId: nonEmptyString(event.pendingProposalId) ?? undefined,
  state: event.visibleState
});

export const buildTopologyWorkbenchBindingDraft = (
  base: TopologyOverlayBaseschema
): TopologyWorkbenchBindingDraft => {
  const params: Record<string, string | boolean> = {};
  Object.entries(sanitizeRecord(base.params) ?? {}).forEach(([key, value]) => {
    if (typeof value === 'string' || typeof value === 'boolean') {
      params[key] = value;
      return;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      params[key] = String(value);
    }
  });

  return {
    viewId: base.viewId,
    scopeType: base.scope.scopeType,
    scopeId: base.scope.scopeId,
    params,
    limits: sanitizeRecord(base.limits) ?? {},
    asOf: serializeDateLike(base.asOf)
  };
};

export const shouldBlockTopologyWorkbenchSwitch = (
  session: VisibilitySession | null,
  nextResumeKey: string | null
): boolean =>
  Boolean(
    session?.pendingProposalId &&
      session.resumeKey &&
      nextResumeKey &&
      session.resumeKey !== nextResumeKey
  );

export const visibilitySessionStateToOverlayState = (
  state: VisibilitySessionStateschema | undefined | null
): TopologyOverlayRuntimeState => {
  if (!state) {
    return emptyTopologyOverlayState();
  }
  return hydrateTopologyOverlayState(
    {
      schema: TopologyArtifactOverlayStateschemaSchemaEnum.TopologyArtifactOverlayStateV1,
      base: state.base,
      revision: state.revision,
      selection: state.selection,
      layers: state.layers,
      graphOverlays: state.graphOverlays
    } as any,
    state.base
  );
};

export const deriveVisibilitySessionOverlayState = (
  session: VisibilitySession | null,
  proposal: VisibilityProposal | null = null
): TopologyOverlayRuntimeState => {
  const baseState = visibilitySessionStateToOverlayState(session?.state);
  if (
    !session?.pendingProposalId ||
    session.mode !== 'preview' ||
    !proposal?.id ||
    proposal.id !== session.pendingProposalId
  ) {
    return baseState;
  }
  return applyTopologyOverlayPatch(baseState, proposal.patch, {
    revision: baseState.revision + 1
  });
};

const overlayBaseSignature = (base?: TopologyOverlayBaseschema): string =>
  base
    ? stableJson({
        source: base.source,
        viewId: base.viewId,
        scope: base.scope,
        params: base.params ?? {},
        limits: base.limits ?? {},
        timeMode: base.timeMode,
        asOf: serializeDateLike(base.asOf)
      })
    : '';

const visibleLayerSignature = (state: TopologyOverlayRuntimeState): string => {
  const hidden = new Set(state.localHiddenLayerIds ?? []);
  return state.layers
    .filter((layer) => layer.visible !== false && !hidden.has(layer.id))
    .map((layer) => layer.id)
    .sort()
    .join('\n');
};

export const shouldPreserveWorkbenchRuntimeGraph = (
  currentSession: VisibilitySession | null,
  currentState: TopologyOverlayRuntimeState,
  nextState: TopologyOverlayRuntimeState,
  nextPendingProposalId?: string | null
): boolean => {
  if (!currentSession?.pendingProposalId) {
    return false;
  }
  if (nonEmptyString(nextPendingProposalId)) {
    return false;
  }
  if (overlayBaseSignature(currentState.base) !== overlayBaseSignature(nextState.base)) {
    return false;
  }
  const currentLayers = visibleLayerSignature(currentState);
  const nextLayers = visibleLayerSignature(nextState);
  return currentLayers.length > 0 && currentLayers === nextLayers;
};

export const parseTopologyWorkbenchStreamFrame = (
  raw: string,
  eventType?: string
): TopologyWorkbenchStreamFrame | null => {
  const parsed = JSON.parse(raw);
  const frame = isRecord(parsed) ? (parsed as Record<string, unknown>) : null;
  if (!frame) {
    return null;
  }
  const payload = isRecord(frame.data) ? frame.data : null;
  if (!payload) {
    return null;
  }
  const type =
    (typeof eventType === 'string' ? eventType.trim() : '') ||
    (typeof frame.type === 'string' ? frame.type.trim() : '');
  return {
    type,
    payload,
    seq: typeof frame.seq === 'number' && Number.isFinite(frame.seq) ? frame.seq : undefined
  };
};
export const readTopologyWorkbenchResumeState = (
  orgId: string,
  resumeKey: string
): TopologyWorkbenchResumeState | null => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(buildStorageKey(orgId, resumeKey));
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const sessionId = nonEmptyString(parsed.sessionId) ?? undefined;
    const threadId = nonEmptyString(parsed.threadId) ?? undefined;
    const bindingHash = nonEmptyString(parsed.bindingHash) ?? undefined;
    if (!sessionId && !threadId) {
      return null;
    }
    return {
      sessionId,
      threadId,
      bindingHash
    };
  } catch {
    return null;
  }
};

export const writeTopologyWorkbenchResumeState = (
  orgId: string,
  resumeKey: string,
  value: TopologyWorkbenchResumeState
): void => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  window.localStorage.setItem(
    buildStorageKey(orgId, resumeKey),
    JSON.stringify({
      sessionId: value.sessionId,
      threadId: value.threadId,
      bindingHash: value.bindingHash
    })
  );
};

export const clearTopologyWorkbenchResumeState = (orgId: string, resumeKey: string): void => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return;
  }
  window.localStorage.removeItem(buildStorageKey(orgId, resumeKey));
};
